import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { APP_HEADER } from '@/shared/components/layout';

import { DOCS_SHELL } from '../components/docsShell.constant';
import { DocsHeader } from '../components/DocsHeader';
import { DocsSearchDialog } from '../components/DocsSearchDialog';
import { DocsSearchTrigger } from '../components/DocsSearchTrigger';
import { DocsSidebar } from '../components/DocsSidebar';
import { useDocsSearch } from '../hooks/useDocsSearch';

/**
 * 문서 상세 셸 — 전용 헤더 / 사이드바 / 본문 / (본문 안의) 목차.
 *
 * 마케팅 헤더가 아니라 `DocsHeader` 를 쓴다. 메가메뉴가 문서 위로 펼쳐지면서
 * sticky 사이드바·목차와 겹치는 것을 피하려는 것이고, 검색·문서목록 같은 문서
 * 전용 동작이 헤더에 있어야 모바일에서 손이 닿는다.
 *
 * 본문 폭을 `max-w-*` 로 묶지 않는다. 적산 화면 캡처가 가로로 넓어서 720px 로 가두면
 * 표의 숫자를 못 읽는다. 대신 캡처·표 컴포넌트가 각자 자기 안에서 가로 스크롤한다.
 *
 * 셸도 **뷰포트 끝까지** 쓴다. 헤더는 full-bleed 인데 셸만 가운데로 모으면 넓은
 * 화면에서 사이드바가 로고 아래가 아닌 곳에 선다. 열 치수는 `DOCS_SHELL` 한
 * 곳에서 헤더와 함께 본다.
 */
export function DocsLayoutView() {
  const { open, openSearch, closeSearch } = useDocsSearch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  // 문서를 이동하면 드로어는 닫힌다. effect 대신 파생 상태 — 라우트가 바뀐
  // *그 렌더*에 바로 닫혀서 한 프레임 열린 채로 남지 않는다(Header 와 같은 패턴).
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setDrawerOpen(false);
  }

  return (
    <div className="flex min-h-screen flex-col">
      <DocsHeader
        onSearch={openSearch}
        drawerOpen={drawerOpen}
        onToggleDrawer={() => setDrawerOpen((prev) => !prev)}
      />

      {drawerOpen && (
        <div
          id="docs-sidebar-drawer"
          className="border-b border-neutral-200 px-3 py-4 lg:hidden dark:border-white/12"
        >
          <DocsSidebar onNavigate={() => setDrawerOpen(false)} />
        </div>
      )}

      <div className="w-full flex-1">
        <div className={`grid grid-cols-1 ${DOCS_SHELL.grid}`}>
          <aside className="hidden border-r border-neutral-200 lg:block dark:border-white/12">
            <div
              className={`sticky ${APP_HEADER.stickyTop} ${APP_HEADER.viewportRest} overflow-y-auto px-3.5 py-6`}
            >
              <DocsSearchTrigger
                onClick={openSearch}
                className="mb-5 lg:hidden"
              />
              <DocsSidebar />
            </div>
          </aside>

          {/* key: 문서가 바뀌면 본문 상태(스크롤 위치·목차)를 새로 시작한다 */}
          <div className="min-w-0" key={pathname}>
            <Outlet />
          </div>
        </div>
      </div>

      {open && <DocsSearchDialog onClose={closeSearch} />}
    </div>
  );
}
